import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { QuickUrlDataService } from 'src/app/services';
import { IQuickUrl } from './quick-url.model';
import { CollectionState } from './collection-state';

@Injectable({ providedIn: 'root' })
export class QuickUrlService {
  private readonly state = new CollectionState<IQuickUrl>();

  public readonly quickUrls$ = this.state.items$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;

  public constructor(private readonly quickUrlDataService: QuickUrlDataService) {}

  public load(): Observable<void> {
    this.state.setLoading(true);

    return this.quickUrlDataService.getQuickUrls().pipe(
      tap({
        next: (quickUrls) => {
          this.state.set(quickUrls);
        },
        error: (error) => {
          const statusText =
            typeof error === 'object' && error !== undefined && 'statusText' in error
              ? String((error as { statusText?: unknown }).statusText)
              : 'Unknown error';

          this.state.setError(statusText);
        },
      }),
      map(() => {
        return undefined;
      }),
      catchError(() => of(undefined))
    );
  }

  public create(quickUrl: IQuickUrl): Observable<void> {
    return this.quickUrlDataService.postQuickUrls(quickUrl).pipe(
      tap((result) => {
        this.state.add(result);
      }),
      map(() => undefined)
    );
  }

  public update(quickUrl: IQuickUrl): Observable<void> {
    return this.quickUrlDataService.postQuickUrls(quickUrl).pipe(
      tap((result) => {
        this.state.replaceWhere((quickUrl) => quickUrl.shortname === result.shortname, result);
      }),
      map(() => undefined)
    );
  }

  public delete(shortName: string): Observable<void> {
    return this.quickUrlDataService.deleteQuickUrl(shortName).pipe(
      tap(() => {
        this.state.removeWhere((quickUrl) => quickUrl.shortname === shortName);
      }),
      map(() => undefined)
    );
  }

  public generateShortname(label: string): string {
    return label.toLowerCase().split(' ').join('-');
  }
}
